'use client';

import HTMLReactParser from 'html-react-parser';
import Link from 'next/link';
import { type FC, useEffect, useState } from 'react';

import SearchPagination from 'components/ListingHeader/pagination';
import styles from 'components/ListingHeader/searchBox.module.scss';

import { isNonEmptyArray, replaceFirstPathSegment } from 'utils/functions';

import type { SearchResponse } from 'components/Search/Listing/utils/retrieveSearch';

export interface SearchBoxProps {
  loading: boolean;
  filteredItems: SearchResponse['results'];
  search?: string;
}

const SearchBox: FC<SearchBoxProps> = ({ loading, filteredItems, search = '' }) => {
  const [page, setPage] = useState(1);
  const [emptySearch, setEmptySearch] = useState(true);
  const pageSize = 5;
  const start = (page - 1) * pageSize;
  const postsToShow = filteredItems.slice(start, start + pageSize);
  const postsExist = isNonEmptyArray(filteredItems);

  useEffect(() => {
    setEmptySearch(!search);
    setPage(1);
  }, [search]);

  return (
    <div className={styles.searchBoxWrapper}>
      <div id="listing-results" className={styles.results}>
        {!loading && emptySearch && <p className={styles.notFound}>Start typing to search...</p>}
        {!loading && !emptySearch && !postsExist && <p className={styles.notFound}>No results found.</p>}
        <div className={styles.cardWrapper}>
          {!loading && !emptySearch && postsExist && (
            <div>
              {postsToShow.map((entry, index) => {
                const { heading, description, slug, id } = {
                  heading: entry?.title,
                  description: entry?.body,
                  slug: entry?.slug || null,
                  id: entry?.builderId || null,
                };

                let updatedDescription = description
                  ? `${description.slice(0, 200)}${description.length > 200 ? '...' : ''}`
                  : '';

                if (updatedDescription.includes('http') || updatedDescription.includes('https')) {
                  updatedDescription = '';
                }

                const parseTitle = heading ? HTMLReactParser(heading) : '';
                const parsedDescription = updatedDescription ? HTMLReactParser(updatedDescription) : '';
                const updatedUrl = replaceFirstPathSegment(slug || '', 'healthcare-insights') ?? '';

                return (
                  <article className={styles.hit} key={`${id}-${index}`}>
                    {parseTitle && (
                      <h2 className={styles.hitTitle}>
                        <Link href={updatedUrl || ''}>{heading && <span>{parseTitle}</span>}</Link>
                      </h2>
                    )}
                    {parsedDescription && <div className={styles.hitDescription}>{parsedDescription}</div>}
                  </article>
                );
              })}
            </div>
          )}
        </div>
        {!loading && !emptySearch && postsExist && (
          <SearchPagination
            totalItems={filteredItems.length}
            limit={pageSize}
            currentPage={page}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
};

export default SearchBox;
